import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ServisfyLogo } from "../../components/Common/ServisfyLogo";

// Public contact-verification landing page (Slice A). Reads ?ref & token from the emailed link and
// confirms the token via the public API. Both the verify and resend endpoints are non-enumerating, so
// this page shows the SAME generic confirmation regardless of validity — it never reveals whether an
// application or token exists, and never claims a staging-redirected email reached the applicant.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const PartnerVerify = () => {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | done | error
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent | error

  const verify = useCallback(async () => {
    setState("verifying");
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/public/partners/applications/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference: ref, token }),
      });
      setState(res.ok ? "done" : "error");
    } catch (_e) {
      setState("error");
    }
  }, [ref, token]);

  useEffect(() => { verify(); }, [verify]);

  const resend = async () => {
    setResendState("sending");
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/public/partners/applications/resend`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference: ref }),
      });
      setResendState(res.ok ? "sent" : "error");
    } catch (_e) {
      setResendState("error");
    }
  };

  return (
    <div className="servisfy-brand min-h-screen text-foreground bg-background bg-gradient-to-br from-background via-secondary/40 to-accent/40" data-theme="light">
      <header className="max-w-4xl mx-auto px-6 pt-6">
        <div className="flex justify-end items-center mb-4">
          <Link to="/partners" className="text-sm font-medium text-primary hover:underline underline-offset-2 whitespace-nowrap">
            ← Partner Program
          </Link>
        </div>
        <div className="flex items-center justify-center" data-testid="verify-brand">
          <ServisfyLogo variant="full" size="xxl" className="mix-blend-multiply" />
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 py-16 text-center">
        {state === "verifying" && (
          <p className="text-muted-foreground" role="status">Verifying your email…</p>
        )}

        {state === "done" && (
          <div data-testid="verify-done">
            <h2 className="text-2xl font-bold text-foreground mb-2">Thank you</h2>
            <p className="text-muted-foreground">
              If the link was valid, your contact details are now verified and your application will
              proceed to review. You can close this page.
            </p>
          </div>
        )}

        {state === "error" && (
          <div data-testid="verify-error">
            <h2 className="text-2xl font-bold text-foreground mb-2">We couldn't verify that link</h2>
            <p className="text-muted-foreground mb-6">
              The link may have expired or already been used. You can request a new verification email
              below.
            </p>
            {ref && (
              <div className="space-y-2">
                <button onClick={resend} disabled={resendState === "sending" || resendState === "sent"}
                  className="bg-brand-gradient hover:brightness-110 disabled:opacity-50 text-white font-medium px-5 py-2.5 rounded-lg transition-all">
                  {resendState === "sending" ? "Sending…" : resendState === "sent" ? "Email sent" : "Resend verification email"}
                </button>
                {resendState === "sent" && (
                  <p className="text-sm text-muted-foreground">
                    If your application is still awaiting verification, a new link has been sent.
                  </p>
                )}
                {resendState === "error" && (
                  <p className="text-sm text-red-600" role="alert">Please try again in a little while.</p>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default PartnerVerify;
